/**
 * Driving a script's Worker from the main thread.
 *
 * ## The timeout has to live out here
 *
 * A script is one synchronous call. `while (true) {}` inside it cannot be
 * interrupted by anything running on the same thread — a timer set inside the
 * Worker is checked by code that never gets to run again. The only thing that
 * stops it is `terminate()` from outside, so the deadline is held here and the
 * Worker is disposable by design.
 *
 * A terminated Worker is gone. The next run builds a new one, which is why the
 * factory is a factory and not an instance.
 *
 * ## Three consecutive timeouts and the study switches itself off
 *
 * Without that, a script that hangs on every redraw burns a core for as long as
 * the chart is open — quietly, during the trading session, on the machine the
 * trader is working on. Turning it off and SAYING SO is the only version of
 * this that a trader can act on.
 *
 * ## Two refusals that happen before the Worker is even built
 *
 * A script whose source no longer matches the fingerprint recorded at install,
 * and a script that has been taken down. Both are consequences of scripts being
 * shareable: the first is "this is not the code you reviewed", the second is
 * "this was removed after you installed it". Neither is a reason to run
 * anything and then discard the result — the check comes first.
 */
import {
  MAX_SCRIPT_TIMEOUTS, SCRIPT_TIMEOUT_MS, scriptFingerprint, validateScriptOutput,
  type ScriptOutput, type ScriptPayload, type ScriptPlotDecl,
} from '@aegis/core';

/** The bit of `Worker` this uses. Narrow, so a test can supply a fake. */
export interface ScriptWorkerLike {
  postMessage(message: unknown): void;
  terminate(): void;
  onmessage: ((event: { data: unknown }) => void) | null;
  onerror: ((event: unknown) => void) | null;
}

export type ScriptWorkerFactory = () => ScriptWorkerLike;

/** Timers, injectable so tests do not wait in real time. */
export interface Scheduler {
  set(fn: () => void, ms: number): number;
  clear(handle: number): void;
}

const REAL_TIMERS: Scheduler = {
  set: (fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number,
  clear: (h) => { globalThis.clearTimeout(h); },
};

export interface ScriptRunRequest {
  readonly source: string;
  /** The fingerprint recorded when this script was installed. */
  readonly fingerprint: string;
  readonly status: 'private' | 'shared' | 'removed';
  readonly plots: readonly ScriptPlotDecl[];
  readonly payload: ScriptPayload;
}

export type ScriptRunOutcome =
  | { readonly status: 'ok'; readonly output: ScriptOutput }
  /** The script ran and failed: a throw, a bad return, a compile error. */
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'timeout'; readonly message: string }
  /** Switched off after too many timeouts. Stays off until `reset()`. */
  | { readonly status: 'disabled'; readonly message: string }
  /** Never started: taken down, or not the code that was installed. */
  | { readonly status: 'refused'; readonly message: string }
  /** A newer run replaced this one before it finished. Not an error. */
  | { readonly status: 'superseded' };

export interface ScriptRunner {
  run(request: ScriptRunRequest): Promise<ScriptRunOutcome>;
  /** Consecutive timeouts so far. Zero after any successful run. */
  timeouts(): number;
  /** Clear a disabled state — the trader asking to try again. */
  reset(): void;
  dispose(): void;
}

export interface ScriptRunnerOptions {
  readonly timeoutMs?: number;
  readonly maxTimeouts?: number;
  readonly scheduler?: Scheduler;
}

export function createScriptRunner(
  factory: ScriptWorkerFactory,
  options: ScriptRunnerOptions = {},
): ScriptRunner {
  const timeoutMs = options.timeoutMs ?? SCRIPT_TIMEOUT_MS;
  const maxTimeouts = options.maxTimeouts ?? MAX_SCRIPT_TIMEOUTS;
  const timers = options.scheduler ?? REAL_TIMERS;

  let worker: ScriptWorkerLike | null = null;
  let consecutiveTimeouts = 0;
  let disposed = false;

  /** The run currently in flight. Its nonce is the only reply accepted. */
  let inflight: {
    nonce: string;
    settle: (o: ScriptRunOutcome) => void;
    timer: number;
  } | null = null;

  /** The request the in-flight run was made with, for validating its reply. */
  let pending: ScriptRunRequest | null = null;

  let counter = 0;
  const nextNonce = (): string => {
    counter += 1;
    return `r${counter}`;
  };

  function kill(): void {
    if (worker) {
      worker.onmessage = null;
      worker.onerror = null;
      worker.terminate();
      worker = null;
    }
  }

  function finish(outcome: ScriptRunOutcome): void {
    const run = inflight;
    if (!run) return;
    inflight = null;
    timers.clear(run.timer);
    run.settle(outcome);
  }

  /** What happens when a run overruns. Named, because two places arm it. */
  function onDeadline(): void {
    /* Nothing short of terminate() stops a synchronous loop. */
    kill();
    consecutiveTimeouts += 1;
    const tooMany = consecutiveTimeouts >= maxTimeouts;
    finish(tooMany
      ? { status: 'disabled', message: disabledMessage(maxTimeouts) }
      : { status: 'timeout', message: `The script did not finish within ${timeoutMs}ms.` });
  }

  function ensureWorker(): ScriptWorkerLike {
    if (worker) return worker;
    const w = factory();
    w.onmessage = (event): void => {
      const data = event.data as { nonce?: unknown; ok?: unknown; value?: unknown; error?: unknown };
      /* A reply for a run that is no longer the current one. Dropped rather
         than applied: it belongs to a study configuration the trader has
         already changed, and drawing it would put stale values on the chart. */
      if (!data || typeof data.nonce !== 'string' || data.nonce !== inflight?.nonce) return;

      /* The Worker has picked this run up. Runs are serial inside one thread,
         so without this the deadline would be counting time the script spent
         WAITING behind the previous redraw — and a chart that ticks faster
         than a heavy script runs would switch a perfectly good study off. */
      if ((data as { started?: unknown }).started === true) {
        timers.clear(inflight.timer);
        inflight.timer = timers.set(onDeadline, timeoutMs);
        return;
      }

      if (data.ok !== true) {
        const message = typeof data.error === 'string' ? data.error : 'The script failed.';
        finish({ status: 'error', message });
        return;
      }

      const request = pending;
      const check = validateScriptOutput(
        data.value, request?.plots ?? [], request?.payload.bars.time.length ?? 0,
      );
      if (!check.ok || !check.output) {
        finish({ status: 'error', message: check.error ?? 'The script returned something unusable.' });
        return;
      }
      consecutiveTimeouts = 0;
      finish({ status: 'ok', output: check.output });
    };
    w.onerror = (): void => {
      /* An error the Worker could not report itself — a failure while loading
         the Worker script. The thread is not trustworthy afterwards. */
      kill();
      finish({ status: 'error', message: 'The sandbox stopped unexpectedly.' });
    };
    worker = w;
    return w;
  }

  return {
    async run(request: ScriptRunRequest): Promise<ScriptRunOutcome> {
      if (disposed) return { status: 'refused', message: 'The runner is closed.' };

      if (request.status === 'removed') {
        return {
          status: 'refused',
          message: 'This script was taken down after you installed it, so it no longer runs.',
        };
      }
      /* Recomputed here, not trusted from the record. The point of the check is
         to catch the source and the fingerprint disagreeing. */
      if (scriptFingerprint(request.source) !== request.fingerprint) {
        return {
          status: 'refused',
          message: 'This script’s code has changed since you installed it. Review it again to keep using it.',
        };
      }
      if (consecutiveTimeouts >= maxTimeouts) {
        return { status: 'disabled', message: disabledMessage(maxTimeouts) };
      }

      /* A newer run supersedes an older one. The old promise is settled rather
         than left hanging, so a caller awaiting it can stop waiting. */
      finish({ status: 'superseded' });

      const nonce = nextNonce();
      pending = request;

      return new Promise<ScriptRunOutcome>((resolve) => {
        inflight = { nonce, settle: resolve, timer: timers.set(onDeadline, timeoutMs) };

        try {
          ensureWorker().postMessage({ nonce, source: request.source, payload: request.payload });
        } catch (e) {
          kill();
          finish({
            status: 'error',
            message: e instanceof Error ? e.message : 'The sandbox could not be started.',
          });
        }
      });
    },

    timeouts: () => consecutiveTimeouts,

    reset(): void {
      consecutiveTimeouts = 0;
    },

    dispose(): void {
      disposed = true;
      finish({ status: 'superseded' });
      kill();
    },
  };
}

function disabledMessage(max: number): string {
  return `This script timed out ${max} times in a row and has been switched off. Left running it would keep a processor busy for the whole session.`;
}
